import React, { useRef, useState, useEffect } from "react";

// 섹션 5-6: 설계내역 연쇄등록 데모 섹션 (간소화)
const Section5and6: React.FC = () => {
  const demoSectionRef = useRef<HTMLElement>(null);
  const [currentTab, setCurrentTab] = useState("specification");

  // 테이블 데이터 타입 정의 - 실제 이미지 데이터 구조에 맞게 수정
  interface SpecificationItem {
    id: string;
    level: string;
    name: string;
    spec: string;
    unit: string;
    quantity: number;
    total: { unitPrice: number; amount: number };
    material: { unitPrice: number; amount: number };
    labor: { unitPrice: number; amount: number };
    expense: { unitPrice: number; amount: number };
    chainType?: string;
  }

  interface UnitPriceItem {
    id: string;
    code: string;
    name: string;
    spec: string;
    unit: string;
    total: { unitPrice: number | null; amount: number };
    material: { unitPrice: number | null; amount: number };
    labor: { unitPrice: number | null; amount: number };
    expense: { unitPrice: number | null; amount: number };
    isMainItem: boolean;
    parentId: string;
    note: string;
  }

  interface PriceCalculationItem {
    id: string;
    title: string;
    description: string;
    details: Array<{
      step: string;
      name: string;
      formula: Record<string, string>;
      costs: string;
      visible?: boolean; // 단계별 표시를 위한 필드
    }>;
    total: number;
    material: number;
    labor: number;
    expense: number;
    parentId: string;
  }

  interface LaborCostItem {
    id: string;
    no: number;
    name: string;
    spec: string;
    unit: string;
    unitPrice: number;
    appliedPrice: number;
    note: string;
    revision: string;
    method: string;
    date: string;
    revisionNote: string;
    parentId: string;
  }

  interface MaterialItem {
    id: string;
    no: number;
    name: string;
    spec: string;
    unit: string;
    priceInfo: { unitPrice: number; page: string };
    appliedPrice: number;
    note: string;
    category: string;
    method: string;
    date: string;
    parentId: string;
  }

  // 건설기계 인터페이스 추가
  interface ConstructionMachineItem {
    id: string;
    name: string;
    spec: string;
    calculation: string;
    total: number;
    material: number;
    labor: number;
    expense: number;
    note: string;
    parentId: string;
    isMainItem: boolean;
  }

  // 초기 빈 테이블 상태 - 건설기계 추가
  const [tableData, setTableData] = useState<{
    specification: SpecificationItem[];
    unitPrice: UnitPriceItem[];
    priceCalculation: PriceCalculationItem[];
    constructionMachine: ConstructionMachineItem[];
    laborCost: LaborCostItem[];
    material: MaterialItem[];
  }>({
    specification: [],
    unitPrice: [],
    priceCalculation: [],
    constructionMachine: [],
    laborCost: [],
    material: []
  });

  // 전체 데이터 구조 (애니메이션용)
  const fullTableData = {
    specification: [
      {
        id: "spec-1",
        level: "1",
        name: "합판거푸집 설치 및 해체",
        spec: "복잡 3회, 수직고 7m까지",
        unit: "㎡",
        quantity: 1,
        total: { unitPrice: 106416, amount: 106416 },
        material: { unitPrice: 49478, amount: 49478 },
        labor: { unitPrice: 56938, amount: 56938 },
        expense: { unitPrice: 0, amount: 0 },
        chainType: "unit-price"
      },
      {
        id: "spec-2", 
        level: "2",
        name: "터파기",
        spec: "백고(0.2㎥)90%+인력10%",
        unit: "㎥",
        quantity: 1,
        total: { unitPrice: 8434, amount: 8434 },
        material: { unitPrice: 396, amount: 396 },
        labor: { unitPrice: 7349, amount: 7349 },
        expense: { unitPrice: 689, amount: 689 },
        chainType: "price-calculation"
      },
      {
        id: "spec-3", 
        level: "3",
        name: "콘크리트 타설",
        spec: "21-24-12, 타설고 3m이하",
        unit: "㎥",
        quantity: 1,
        total: { unitPrice: 75230, amount: 75230 },
        material: { unitPrice: 68450, amount: 68450 },
        labor: { unitPrice: 6780, amount: 6780 },
        expense: { unitPrice: 0, amount: 0 },
        chainType: "unit-price"
      }
    ],
    unitPrice: [
      // 제3호표: 합판거푸집 설치 및 해체 (부모행)
      {
        id: "unit-main-3",
        code: "제 3호표",
        name: "합판거푸집 설치 및 해체",
        spec: "복잡 3회, 수직고 7m까지", 
        unit: "㎡",
        total: { unitPrice: null, amount: 106416 },
        material: { unitPrice: null, amount: 49478 },
        labor: { unitPrice: null, amount: 56938 },
        expense: { unitPrice: null, amount: 0 },
        isMainItem: true,
        parentId: "spec-1",
        note: "공통 6-3-1"
      },
      {
        id: "unit-3-1",
        code: "-",
        name: "합판거푸집 - 자재비",
        spec: "3회",
        unit: "M2", 
        total: { unitPrice: 48909, amount: 48909 },
        material: { unitPrice: 48909, amount: 48909 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "spec-1",
        note: "제1호표 참조"
      },
      {
        id: "unit-3-2",
        code: "-",
        name: "합판거푸집 - 인력투입",
        spec: "복잡, 수직고 7m까지",
        unit: "M2",
        total: { unitPrice: 57507, amount: 57507 },
        material: { unitPrice: 569, amount: 569 },
        labor: { unitPrice: 56938, amount: 56938 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "spec-1",
        note: "제2호표 참조"
      },
      
      // 제1호표: 합판거푸집 - 자재비 (부모행)
      {
        id: "unit-main-1",
        code: "제 1호표",
        name: "합판거푸집 - 자재비",
        spec: "3회", 
        unit: "㎡",
        total: { unitPrice: null, amount: 48909 },
        material: { unitPrice: null, amount: 48909 },
        labor: { unitPrice: null, amount: 0 },
        expense: { unitPrice: null, amount: 0 },
        isMainItem: true,
        parentId: "unit-3-1",
        note: "공통 6-3-1"
      },
      {
        id: "unit-1-1",
        code: "-",
        name: "내수합판",
        spec: "내수합판, 1급, 12×1220×2440mm",
        unit: "㎡", 
        total: { unitPrice: 10986, amount: 11315 },
        material: { unitPrice: 10986, amount: 11315 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-1",
        note: ""
      },
      {
        id: "unit-1-2",
        code: "-",
        name: "각재",
        spec: "각재, 외송",
        unit: "㎥",
        total: { unitPrice: 528153, amount: 20069 },
        material: { unitPrice: 528153, amount: 20069 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-1",
        note: ""
      },
      {
        id: "unit-1-3",
        code: "-",
        name: "적용비율",
        spec: "재료비의",
        unit: "%",
        total: { unitPrice: 31384, amount: 13903 },
        material: { unitPrice: 31384, amount: 13903 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-1",
        note: ""
      },
      {
        id: "unit-1-4",
        code: "-",
        name: "소모자재(박리재 등)",
        spec: "재료비의",
        unit: "%",
        total: { unitPrice: 45287, amount: 3622 },
        material: { unitPrice: 45287, amount: 3622 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-1",
        note: ""
      },
      
      // 제2호표: 합판거푸집 - 인력투입 (부모행)
      {
        id: "unit-main-2",
        code: "제 2호표",
        name: "합판거푸집 - 인력투입",
        spec: "복잡, 수직고 7m까지", 
        unit: "㎡",
        total: { unitPrice: null, amount: 57507 },
        material: { unitPrice: null, amount: 569 },
        labor: { unitPrice: null, amount: 56938 },
        expense: { unitPrice: null, amount: 0 },
        isMainItem: true,
        parentId: "unit-3-2",
        note: "공통 6-3-1"
      },
      {
        id: "unit-2-1",
        code: "-",
        name: "형틀목공",
        spec: "일반공사 직종",
        unit: "인", 
        total: { unitPrice: 272831, amount: 45562 },
        material: { unitPrice: 0, amount: 0 },
        labor: { unitPrice: 272831, amount: 45562 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-2",
        note: ""
      },
      {
        id: "unit-2-2",
        code: "-",
        name: "보통인부",
        spec: "일반공사 직종",
        unit: "인",
        total: { unitPrice: 169804, amount: 11376 },
        material: { unitPrice: 0, amount: 0 },
        labor: { unitPrice: 169804, amount: 11376 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-2",
        note: ""
      },
      {
        id: "unit-2-3",
        code: "-",
        name: "공구손료",
        spec: "노무비의",
        unit: "%",
        total: { unitPrice: 56938, amount: 569 },
        material: { unitPrice: 56938, amount: 569 },
        labor: { unitPrice: 0, amount: 0 },
        expense: { unitPrice: 0, amount: 0 },
        isMainItem: false,
        parentId: "unit-main-2"
      }
    ],
    priceCalculation: [
      {
        id: "calc-1",
        title: "터파기 (백토(0.2m)90%+인력10%) [m]",
        description: "",
        details: [
          {
            step: "1",
            name: "1.기계(90%) (0.2m) - 굴삭기(무한궤도)",
            formula: {
              q: "0.2",
              f: "1/1.24 = 0.81",
              K: "0.9", 
              E: "(0.7+0.6)/2-0.05 = 0.6",
              Cm: "18 sec(135°)",
              Q: "(3600×q×K×f×E)/Cm = 17.5 ㎥/hr",
              r: "0.9"
            },
            costs: "굴삭기(무한궤도) (0.2㎥) - 재료비: 7,701/Q×r = 396, 노무비: 57,077/Q×r = 2,935, 경비: 13,399/Q×r = 689, 합계: 4,020",
            visible: false
          },
          {
            step: "2", 
            name: "2.인력(10%) ((보통토, 경질토, 자갈토)의 평균)",
            formula: {
              n: "3 인",
              r: "0.1",
              "계산식": "보통인부: 169,804×(0.2+0.26+0.32)/n×r = 4,414"
            },
            costs: "보통인부 투입비용: 4,414원",
            visible: false
          },
          {
            step: "3", 
            name: "3.합계",
            formula: {
              "재료비": "396",
              "노무비": "7,349", 
              "경비": "689",
              "합계": "8,434"
            },
            costs: "최종 합계 산출",
            visible: false
          }
        ],
        total: 8434,
        material: 396,
        labor: 7349,
        expense: 689,
        parentId: "spec-2"
      }
    ],
    laborCost: [
      {
        id: "labor-1",
        no: 1,
        name: "보통인부",
        spec: "-",
        unit: "인",
        unitPrice: 169804,
        appliedPrice: 169804,
        note: "-",
        revision: "2025년(상)",
        method: "최저단가",
        date: "2025-08-22",
        revisionNote: "-",
        parentId: "spec-2"
      },
      {
        id: "labor-2", 
        no: 2,
        name: "형틀목공",
        spec: "-",
        unit: "인",
        unitPrice: 272831,
        appliedPrice: 272831,
        note: "-",
        revision: "2025년(상)", 
        method: "최저단가",
        date: "2025-08-22",
        revisionNote: "-",
        parentId: "spec-1"
      },
      {
        id: "labor-3", 
        no: 3,
        name: "건설기계운전사",
        spec: "-",
        unit: "인",
        unitPrice: 273971,
        appliedPrice: 273971,
        note: "-",
        revision: "2025년(상)", 
        method: "최저단가",
        date: "2025-08-22",
        revisionNote: "-",
        parentId: "spec-1"
      }
    ],
    constructionMachine: [
      // 상위행: 굴삭기(무한궤도)
      {
        id: "machine-main-1",
        name: "1. 굴삭기(무한궤도)",
        spec: "0.2㎥",
        calculation: "-",
        total: 78177,
        material: 7701,
        labor: 57077,
        expense: 13399,
        note: "-",
        parentId: "spec-2",
        isMainItem: true
      },
      // 하위행 1: 손료
      {
        id: "machine-1-1", 
        name: "손료",
        spec: "-",
        calculation: "64,267,000원×2,085×10(-7)",
        total: 0,
        material: 0,
        labor: 0,
        expense: 13399,
        note: "-",
        parentId: "machine-main-1",
        isMainItem: false
      },
      // 하위행 2: 주연료
      {
        id: "machine-1-2",
        name: "주연료",
        spec: "경유",
        calculation: "5ℓ×1,273",
        total: 0,
        material: 6365,
        labor: 0,
        expense: 0,
        note: "-",
        parentId: "machine-main-1", 
        isMainItem: false
      },
      // 하위행 3: 잡품
      {
        id: "machine-1-3",
        name: "잡품",
        spec: "주연료의",
        calculation: "21% × 6,365",
        total: 0,
        material: 1336,
        labor: 0,
        expense: 0,
        note: "-",
        parentId: "machine-main-1",
        isMainItem: false
      },
      // 하위행 4: 건설기계운전사
      {
        id: "machine-1-4",
        name: "건설기계운전사",
        spec: "-",
        calculation: "1인×273,971×1/8 × 16/12 × 25/20",
        total: 0,
        material: 0,
        labor: 57077,
        expense: 0,
        note: "-",
        parentId: "machine-main-1",
        isMainItem: false
      }
    ],
    material: [
      {
        id: "material-1",
        no: 1,
        name: "경유",
        spec: "저 유 황",
        unit: "ℓ",
        priceInfo: { unitPrice: 1273, page: "2025.04.29(조달청)" },
        appliedPrice: 1273,
        note: "-",
        category: "일반",
        method: "최저단가",
        date: "-",
        parentId: "spec-2"
      },
      {
        id: "material-2",
        no: 2,
        name: "각재",
        spec: "각재, 외송",
        unit: "㎥",
        priceInfo: { unitPrice: 528153, page: "(2024.04)" },
        appliedPrice: 528153,
        note: "-",
        category: "일반",
        method: "최저단가",
        date: "-",
        parentId: "spec-1"
      },
      {
        id: "material-3",
        no: 3,
        name: "내수합판",
        spec: "내수합판, 1급, 12×1220×2440mm",
        unit: "㎡",
        priceInfo: { unitPrice: 10986, page: "(2024.04)" },
        appliedPrice: 10966,
        note: "-",
        category: "일반",
        method: "최저단가",
        date: "-",
        parentId: "spec-1"
      }
    ]
  };

  // 탭 설정 - 건설기계 탭 추가
  const tabConfig = {
    specification: { icon: "📋", title: "설계내역서" },
    "unit-price": { icon: "💰", title: "일위대가(산근)" },
    "price-calculation": { icon: "📊", title: "단가산출(산근)" },
    "construction-machine": { icon: "🏗️", title: "건설기계" },
    "labor-cost": { icon: "👷", title: "노임단가" },
    material: { icon: "🧱", title: "자재단가" },
  };

  // 탭 순서 - 건설기계 탭 추가
  const tabSequence = [
    "specification",
    "unit-price", 
    "price-calculation",
    "construction-machine",
    "labor-cost",
    "material",
  ];

  // 고객 후기
  const testimonials = [
    {
      rating: "★★★★★",
      text: "일위대가 검증 시간이 90% 단축됐어요. 표준품셈 대조 기능으로 정확도가 크게 향상됐습니다.",
      author: "김현수 대표",
      company: "현대건설파트너스",
    },
    {
      rating: "★★★★★", 
      text: "AI 검증 기능이 정말 좋아요. 일위대가 오류를 즉시 발견해서 품질 관리가 완벽해졌습니다.",
      author: "박미영 과장",
      company: "삼성물산",
    },
    {
      rating: "★★★★★",
      text: "복잡한 단가 산출이 이제 클릭 몇 번이면 끝나요. 업무 효율성이 정말 놀라워요.",
      author: "이준호 팀장", 
      company: "포스코건설",
    },
  ];

  // 회사 로고 데이터
  const companyLogos = [
    "🏢 현대건설", "🏗️ 삼성물산", "🏭 포스코건설", "🌟 대우건설", "⚡ SK건설",
    "🔷 롯데건설", "🏘️ GS건설", "🏛️ 한화건설", "🎯 대림산업", "💎 두산건설"
  ];

  // 탭 전환 함수 (애니메이션 전용 - 클릭 비활성화)
  const switchToTab = (tabName: string) => {
    // 클릭을 통한 수동 탭 전환 비활성화
    if (!isAnimating) {
      // Manual tab switching is disabled. Animation will control tabs.
      return;
    }
    
    // Tab switched by animation
    setCurrentTab(tabName);
    
    // 탭 헤더 업데이트
    const contentIcon = document.getElementById("contentIcon");
    const contentTitle = document.getElementById("contentTitle");

    if (contentIcon && contentTitle) {
      const config = tabConfig[tabName as keyof typeof tabConfig];
      contentIcon.textContent = config.icon;
      contentTitle.textContent = config.title;
    }
  };

  // 애니메이션 상태 관리
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [animationStep, setAnimationStep] = useState("");
  const [animationRef, setAnimationRef] = useState<NodeJS.Timeout | null>(null);

  // 새 Phase 1 애니메이션 시나리오 정의 - 실제 요구사항에 따른 정확한 타이밍
  const animationSequences = {
    phase1: [
      // 설계내역서: Add 3 rows with 2-second delays
      { action: "add-data", tab: "specification", items: [fullTableData.specification[0]], delay: 2000 },
      { action: "add-data", tab: "specification", items: [fullTableData.specification[1]], delay: 2000 },
      { action: "add-data", tab: "specification", items: [fullTableData.specification[2]], delay: 2000 },
      
      // 일위대가: Add all 3 호표 in 1-2-3 order with 0.2-second delays, 4-second pause after each group
      { action: "switch-tab", tab: "unit-price", delay: 100 },
      
      // 제1호표 그룹 (부모 + 4개 하위행) - 첫 번째
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[3]], delay: 200 }, // 제1호표 부모
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[4]], delay: 200 }, // 내수합판
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[5]], delay: 200 }, // 각재
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[6]], delay: 200 }, // 적용비율
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[7]], delay: 200 }, // 소모자재
      { action: "wait", delay: 1000 }, // 1-second pause after 제1호표 group
      
      // 제2호표 그룹 (부모 + 3개 하위행) - 두 번째
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[8]], delay: 200 }, // 제2호표 부모
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[9]], delay: 200 }, // 형틀목공
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[10]], delay: 200 }, // 보통인부
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[11]], delay: 200 }, // 공구손료
      { action: "wait", delay: 1000 }, // 1-second pause after 제2호표 group
      
      // 제3호표 그룹 (부모 + 2개 하위행) - 세 번째
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[0]], delay: 200 }, // 제3호표 부모
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[1]], delay: 200 }, // 자재비 하위
      { action: "add-data", tab: "unit-price", items: [fullTableData.unitPrice[2]], delay: 200 }, // 인력투입 하위
      { action: "wait", delay: 2500 }, // 2.5-second pause after all groups
      
      // 단가산출: Tab switch with 1.5-second delay, then add details step by step
      { action: "switch-tab", tab: "price-calculation", delay: 1500 },
      { action: "add-data", tab: "price-calculation", items: fullTableData.priceCalculation, delay: 1000 }, // Initial data
      { action: "reveal-step", step: 1, delay: 1000 }, // Show step 1
      { action: "reveal-step", step: 2, delay: 1000 }, // Show step 2
      { action: "reveal-step", step: 3, delay: 2500 }, // Show step 3 with longer pause
      
      // 건설기계: Tab switch with 1.5-second delay, then add items with 0.2-second delays
      { action: "switch-tab", tab: "construction-machine", delay: 1500 },
      { action: "add-data", tab: "construction-machine", items: [fullTableData.constructionMachine[0]], delay: 200 }, // 굴삭기 상위행
      { action: "add-data", tab: "construction-machine", items: [fullTableData.constructionMachine[1]], delay: 200 }, // 손료
      { action: "add-data", tab: "construction-machine", items: [fullTableData.constructionMachine[2]], delay: 200 }, // 주연료
      { action: "add-data", tab: "construction-machine", items: [fullTableData.constructionMachine[3]], delay: 200 }, // 잡품
      { action: "add-data", tab: "construction-machine", items: [fullTableData.constructionMachine[4]], delay: 2500 }, // 건설기계운전사 + 2.5초 대기
      
      // 노임단가: Tab switch with 1.5-second delay, then add rows with 0.2-second delays
      { action: "switch-tab", tab: "labor-cost", delay: 1500 },
      { action: "add-data", tab: "labor-cost", items: [fullTableData.laborCost[0]], delay: 200 }, // 보통인부
      { action: "add-data", tab: "labor-cost", items: [fullTableData.laborCost[1]], delay: 200 }, // 형틀목공
      { action: "add-data", tab: "labor-cost", items: [fullTableData.laborCost[2]], delay: 2500 }, // 건설기계운전사 + 2.5초 대기
      
      // 자재단가: Tab switch with 1.5-second delay, then add rows with 0.2-second delays
      { action: "switch-tab", tab: "material", delay: 1500 },
      { action: "add-data", tab: "material", items: [fullTableData.material[0]], delay: 200 }, // 경유
      { action: "add-data", tab: "material", items: [fullTableData.material[1]], delay: 200 }, // 각재
      { action: "add-data", tab: "material", items: [fullTableData.material[2]], delay: 2500 }, // 내수합판 + 2.5초 대기
      { action: "wait", delay: 2000 }, // 데이터 표시 후 잠시 대기
      { action: "clear-all-data", delay: 500 }, // 리셋 및 루프
      { action: "switch-tab", tab: "specification", delay: 500 }
    ]
  };

  // 애니메이션 실행 함수
  const executeAnimationStep = async (step: any, phase: number) => {
    setAnimationStep(`Phase ${phase} - ${step.action} (${step.tab || 'processing'})`);
    
    if (step.action === "wait") {
      // wait action - just delay
    } else if (step.action === "reveal-step") {
      // reveal-step action - show specific step in price calculation
      const stepNumber = step.step;
      setTableData(prev => ({
        ...prev,
        priceCalculation: prev.priceCalculation.map(item => ({
          ...item,
          details: item.details.map((detail, index) => ({
            ...detail,
            visible: index < stepNumber // Show steps up to stepNumber
          }))
        }))
      }));
    } else if (step.action === "add-data") {
      if (step.tab === "specification") {
        setTableData(prev => ({
          ...prev,
          specification: [...prev.specification, ...step.items]
        }));
      } else if (step.tab === "unit-price") {
        setTableData(prev => ({
          ...prev,
          unitPrice: [...prev.unitPrice, ...step.items]
        }));
      } else if (step.tab === "labor-cost") {
        setTableData(prev => ({
          ...prev,
          laborCost: [...prev.laborCost, ...step.items]
        }));
      } else if (step.tab === "material") {
        setTableData(prev => ({
          ...prev,
          material: [...prev.material, ...step.items]
        }));
      } else if (step.tab === "price-calculation") {
        setTableData(prev => ({
          ...prev,
          priceCalculation: [...prev.priceCalculation, ...step.items]
        }));
      } else if (step.tab === "construction-machine") {
        setTableData(prev => ({
          ...prev,
          constructionMachine: [...prev.constructionMachine, ...step.items]
        }));
      }
    } else if (step.action === "switch-tab") {
      setCurrentTab(step.tab);
      // 탭 헤더 업데이트
      const contentIcon = document.getElementById("contentIcon");
      const contentTitle = document.getElementById("contentTitle");
      if (contentIcon && contentTitle) {
        const config = tabConfig[step.tab as keyof typeof tabConfig];
        contentIcon.textContent = config.icon;
        contentTitle.textContent = config.title;
      }
    } else if (step.action === "clear-all-data") {
      setTableData({
        specification: [],
        unitPrice: [],
        priceCalculation: [],
        constructionMachine: [],
        laborCost: [],
        material: []
      });
      setCurrentTab("specification");
      // 헤더 리셋
      const contentIcon = document.getElementById("contentIcon");
      const contentTitle = document.getElementById("contentTitle");
      if (contentIcon && contentTitle) {
        contentIcon.textContent = "📋";
        contentTitle.textContent = "설계내역서";
      }
    }
    
    return new Promise(resolve => {
      const timeout = setTimeout(resolve, step.delay);
      setAnimationRef(timeout);
    });
  };

  // 전체 애니메이션 실행
  const startChainAnimation = async () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentPhase(1);
    // Animation started

    const runAnimation = async () => {
      try {
        while (true) {
          // Only Phase 1 - continuous loop
          setCurrentPhase(1);
          for (const step of animationSequences.phase1) {
            await executeAnimationStep(step, 1);
          }
        }
      } catch (error) {
        console.error("Animation error:", error);
        setIsAnimating(false);
      }
    };

    runAnimation();
  };

  // 애니메이션 정지
  const stopAnimation = () => {
    setIsAnimating(false);
    if (animationRef) {
      clearTimeout(animationRef);
      setAnimationRef(null);
    }
    setAnimationStep("Animation stopped");
  };

  // 스크롤 감지를 통한 자동 애니메이션 시작
  useEffect(() => {
    if (!demoSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating) {
            // Demo section entered viewport - starting animation
            startChainAnimation();
          }
        });
      },
      {
        threshold: 0.3, // 섹션이 30% 보일 때 트리거
        rootMargin: '0px'
      }
    );

    observer.observe(demoSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isAnimating]); // isAnimating 의존성 추가

  return (
    <>
      <style>{`
        /* 탭 전환 애니메이션 */
        .tab-content-container {
          animation: tabFadeIn 0.5s ease-in-out;
        }
        
        @keyframes tabFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        /* 데이터 추가 애니메이션 */
        .table-row-enter {
          animation: rowFadeIn 0.6s ease-out;
        }
        
        @keyframes rowFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        /* 단계별 수식 표시 애니메이션 */
        @keyframes fadeInStep {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* 테이블 컨테이너 애니메이션 */
        .animated-table-container {
          transition: all 0.3s ease;
        }
      `}</style>
      
      {/* 5. 설계내역 연쇄등록 데모 섹션 */}
      <section
        className="section demo-section scroll-animate"
        id="demo"
        ref={demoSectionRef}
      >
        <div className="section-container">
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: "rgba(255, 243, 205, 0.8)",
                padding: "8px 20px",
                borderRadius: "30px",
                border: "1px solid rgba(251, 191, 36, 0.3)",
                marginBottom: "20px",
              }}
            >
              <span style={{ fontSize: "16px" }}>🔗</span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  letterSpacing: "0.5px",
                }}
              >
                CHAIN REGISTRATION SYSTEM V2
              </span>
            </div>

            <h2
              style={{
                fontSize: "36px",
                fontWeight: "800",
                background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "20px",
                lineHeight: "1.2",
              }}
            >
              설계내역 연쇄등록
            </h2>

            <p
              style={{
                fontSize: "18px",
                color: "#64748B",
                lineHeight: "1.6",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              설계내역서에서 다른 탭들로 자동 연결되는 혁신적인 시스템
            </p>
          </div>

          {/* 탭 네비게이션 - 모던 디자인 */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginBottom: "40px",
              flexWrap: "wrap",
              padding: "8px",
              backgroundColor: "#F8FAFC",
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
            }}
          >
            {tabSequence.map((tab) => {
              const isActive = currentTab === tab;
              const config = tabConfig[tab as keyof typeof tabConfig];
              
              return (
                <div
                  key={tab}
                  style={{
                    position: "relative",
                    padding: "16px 24px",
                    border: "none",
                    borderRadius: "12px",
                    backgroundColor: isActive ? "#3B82F6" : "transparent",
                    color: isActive ? "#FFFFFF" : "#64748B",
                    cursor: "default", // 클릭 비활성화
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "140px",
                    justifyContent: "center",
                    boxShadow: isActive 
                      ? "0 4px 12px rgba(59, 130, 246, 0.3)" 
                      : "none",
                    transform: isActive ? "translateY(-1px)" : "translateY(0)",
                    opacity: isActive ? 1 : 0.7, // 비활성 탭 투명도 조정
                  }}
                >
                  <span style={{ fontSize: "16px" }}>{config.icon}</span>
                  <span>{config.title}</span>
                  {isActive && (
                    <span 
                      style={{ 
                        fontSize: "12px",
                        opacity: 0.8,
                      }}
                    >
                      ✓
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* 컨텐츠 헤더 */}
          <div 
            className="content-header"
            style={{
              backgroundColor: "#FFFFFF",
              padding: "24px",
              borderRadius: "12px 12px 0 0",
              borderBottom: "3px solid #3B82F6",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
                <span 
                  id="contentIcon" 
                  style={{ 
                    fontSize: "24px",
                    padding: "8px",
                    backgroundColor: "#EFF6FF",
                    borderRadius: "8px",
                  }}
                >
                  📋
                </span>
                <h3 
                  id="contentTitle"
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#1E293B",
                    margin: 0,
                  }}
                >
                  설계내역서
                </h3>
              </div>
              
            </div>
          </div>

          {/* 탭별 컨텐츠 렌더링 */}
          {currentTab === "specification" && (
            <div
              className="table-container tab-content-container animated-table-container"
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "0 0 12px 12px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                borderTop: "none",
              }}
            >
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "950px", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "5%" }}>레벨</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "18%" }}>품명</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "15%" }}>규격</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "5%" }}>단위</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "5%" }}>수량</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "14%" }}>합계</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "14%" }}>재료비</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "14%" }}>노무비</th>
                      <th colSpan={2} style={{ ...headerCellStyle, width: "14%" }}>경비</th>
                    </tr>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.specification.map((item) => (
                      <tr key={item.id} className="table-row-enter" style={{ ...dataRowStyle }}>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.level}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.name}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.spec}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.unit}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.quantity}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.total.unitPrice.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.total.amount.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.material.unitPrice.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.material.amount.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.labor.unitPrice.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.labor.amount.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>{item.expense.unitPrice.toLocaleString()}</td>
                        <td style={{ ...dataCellStyle, textAlign: "right" }}>{item.expense.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === "unit-price" && (
            <div className="table-container tab-content-container animated-table-container" style={{ ...tableContainerStyle }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "900px", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "8%" }}>호표</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "18%" }}>품명</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "13%" }}>규격</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "6%" }}>단위</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "12%" }}>합계</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "12%" }}>재료비</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "12%" }}>노무비</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", width: "12%" }}>경비</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, width: "7%" }}>비고</th>
                    </tr>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>금액</th>
                      <th style={{ ...subHeaderCellStyle, borderRight: "1px solid #E2E8F0", width: "7%" }}>단가</th>
                      <th style={{ ...subHeaderCellStyle, width: "7%" }}>금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.unitPrice.map((item) => (
                      <tr 
                        key={item.id} 
                        className="table-row-enter"
                        style={{ 
                          ...dataRowStyle, 
                          backgroundColor: item.isMainItem ? "#FEF3C7" : "transparent" 
                        }}
                      >
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.code}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.name}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.spec}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.unit}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.total.unitPrice ? item.total.unitPrice.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.total.amount.toLocaleString()}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.material.unitPrice ? item.material.unitPrice.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.material.amount.toLocaleString()}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.labor.unitPrice ? item.labor.unitPrice.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.labor.amount.toLocaleString()}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.expense.unitPrice ? item.expense.unitPrice.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.expense.amount.toLocaleString()}
                        </td>
                        <td style={{ 
                          ...dataCellStyle,
                          textAlign: "center",
                          fontWeight: item.isMainItem && item.note ? "600" : "normal",
                          color: item.isMainItem && item.note ? "#3B82F6" : "inherit"
                        }}>
                          {item.note}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === "price-calculation" && (
            <div className="table-container tab-content-container animated-table-container" style={{ ...tableContainerStyle }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "800px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>산출근거</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>합계</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>재료비</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>노무비</th>
                      <th style={{ ...headerCellStyle }}>경비</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.priceCalculation.map((item) => (
                      <tr key={item.id} className="table-row-enter" style={{ ...dataRowStyle }}>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", padding: "20px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "12px", color: "#1E293B" }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "16px", fontStyle: "italic" }}>
                            {item.description}
                          </div>
                          
                          {item.details.map((detail, index) => 
                            detail.visible !== false ? (
                              <div key={index} style={{ marginBottom: index < item.details.length - 1 ? "20px" : "0", animation: "fadeInStep 0.8s ease-out" }}>
                                <div style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "8px" }}>
                                  {detail.step}. {detail.name}
                                </div>
                                <div style={{ fontSize: "12px", color: "#6B7280", lineHeight: "1.6", marginLeft: "12px" }}>
                                  {Object.entries(detail.formula).map(([key, value], formulaIndex) => (
                                    <span key={formulaIndex}>
                                      {key} = <span style={{ color: "#3B82F6", fontWeight: "600" }}>{value}</span>
                                      {formulaIndex < Object.entries(detail.formula).length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                                  <br/>
                                  {detail.costs}
                                </div>
                              </div>
                            ) : null
                          )}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right", fontSize: "16px", fontWeight: "600", verticalAlign: "top" }}>
                          {item.total.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right", fontSize: "16px", fontWeight: "600", verticalAlign: "top" }}>
                          {item.material.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right", fontSize: "16px", fontWeight: "600", verticalAlign: "top" }}>
                          {item.labor.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, textAlign: "right", fontSize: "16px", fontWeight: "600", verticalAlign: "top" }}>
                          {item.expense.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === "labor-cost" && (
            <div className="table-container tab-content-container animated-table-container" style={{ ...tableContainerStyle }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "800px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>No</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>품명</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>규격</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>단위</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>단가</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>적용단가</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>비고</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>수정</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>적용방법</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>적용일자</th>
                      <th style={{ ...headerCellStyle }}>수정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.laborCost.map((item) => (
                      <tr key={item.id} className="table-row-enter" style={{ ...dataRowStyle }}>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.no}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.name}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.spec}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.unit}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>
                          {item.unitPrice.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>
                          {item.appliedPrice.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.note}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.revision}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.method}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.date}</td>
                        <td style={{ ...dataCellStyle }}>{item.revisionNote}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === "construction-machine" && (
            <div className="table-container tab-content-container animated-table-container" style={{ ...tableContainerStyle }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "800px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>명칭</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>규격</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>산출근거</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>합계</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>재료비</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>노무비</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0" }}>경비</th>
                      <th style={{ ...headerCellStyle }}>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.constructionMachine.map((item) => (
                      <tr 
                        key={item.id} 
                        className="table-row-enter" 
                        style={{ 
                          ...dataRowStyle, 
                          backgroundColor: item.isMainItem ? "#FEF3C7" : "transparent" 
                        }}
                      >
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.name}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.spec}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.calculation}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.total > 0 ? item.total.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.material > 0 ? item.material.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.labor > 0 ? item.labor.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle, 
                          borderRight: "1px solid #E2E8F0", 
                          textAlign: "right",
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.expense > 0 ? item.expense.toLocaleString() : "-"}
                        </td>
                        <td style={{ 
                          ...dataCellStyle,
                          fontWeight: item.isMainItem ? "600" : "normal"
                        }}>
                          {item.note}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {currentTab === "material" && (
            <div className="table-container tab-content-container animated-table-container" style={{ ...tableContainerStyle }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: "1000px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>No</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>품명</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>규격</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>단위</th>
                      <th colSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "center" }}>가격정보</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>적용단가</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>비고</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>구분</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", verticalAlign: "middle" }}>적용방법</th>
                      <th rowSpan={2} style={{ ...headerCellStyle, verticalAlign: "middle" }}>적용일자</th>
                    </tr>
                    <tr style={{ backgroundColor: "#F8FAFC" }}>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", fontSize: "11px" }}>단가</th>
                      <th style={{ ...headerCellStyle, borderRight: "1px solid #E2E8F0", fontSize: "11px" }}>page</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.material.map((item) => (
                      <tr key={item.id} className="table-row-enter" style={{ ...dataRowStyle }}>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.no}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.name}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.spec}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.unit}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>
                          {item.priceInfo.unitPrice.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.priceInfo.page}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0", textAlign: "right" }}>
                          {item.appliedPrice.toLocaleString()}
                        </td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.note}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.category}</td>
                        <td style={{ ...dataCellStyle, borderRight: "1px solid #E2E8F0" }}>{item.method}</td>
                        <td style={{ ...dataCellStyle }}>{item.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 6. REVIEW 섹션 */}
      <section className="section testimonials-section scroll-animate" id="review">
        <div className="section-container">
          <div className="testimonials-header">
            <h2 className="testimonials-title">5,000개 기업이 선택한 이유</h2>
            <p className="testimonials-subtitle">
              대한민국 건설 산업의 디지털 혁신을 이끌고 있습니다
            </p>
          </div>

          {/* 업체 로고 무한 슬라이딩 캐로셀 */}
          <div className="customer-logos">
            <div className="logo-track">
              {/* 첫 번째 세트 */}
              {companyLogos.map((logo, index) => (
                <div key={`first-${index}`} className="logo-item">
                  {logo}
                </div>
              ))}
              {/* 두 번째 세트 (무한 루프용) */}
              {companyLogos.map((logo, index) => (
                <div key={`second-${index}`} className="logo-item">
                  {logo}
                </div>
              ))}
            </div>
          </div>

          {/* 고객 후기 카드 */}
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">{testimonial.rating}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div>
                    <div className="author-name">{testimonial.author}</div>
                    <div className="author-company">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

// 스타일 상수들
const tableContainerStyle = {
  backgroundColor: "#FFFFFF",
  borderRadius: "0 0 12px 12px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  border: "1px solid #E2E8F0",
  borderTop: "none",
};

const headerCellStyle = {
  padding: "16px 12px",
  backgroundColor: "#F8FAFC",
  borderBottom: "2px solid #E2E8F0",
  textAlign: "center" as const,
  fontWeight: "600",
  fontSize: "14px",
  color: "#374151",
  lineHeight: "1.4",
};

const subHeaderCellStyle = {
  padding: "12px 8px",
  backgroundColor: "#F8FAFC", 
  borderBottom: "2px solid #E2E8F0",
  textAlign: "center" as const,
  fontWeight: "600",
  fontSize: "13px",
  color: "#6B7280",
  lineHeight: "1.4",
};

const dataRowStyle = {
  borderBottom: "1px solid #F3F4F6",
};

const dataCellStyle = {
  padding: "14px 12px",
  borderBottom: "1px solid #F3F4F6",
  fontSize: "14px",
  color: "#374151",
  lineHeight: "1.4",
};

export default Section5and6;